import { useCallback, useEffect, useState } from "react";
import { Check, ChevronLeft, ChevronRight, Expand, X } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { HALLS } from "@/constants/neco";

export const Halls = () => {
  const [lightbox, setLightbox] = useState(null); // { images, index, hallName }

  const close = useCallback(() => setLightbox(null), []);
  const step = useCallback(
    (dir) =>
      setLightbox((lb) =>
        lb ? { ...lb, index: (lb.index + dir + lb.images.length) % lb.images.length } : lb
      ),
    []
  );

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, close, step]);

  const openLightbox = (hall, index) =>
    setLightbox({ images: [hall.image, ...(hall.gallery || [])], index, hallName: hall.name });

  return (
  <>
  <section id="salonlar" data-testid={HOME.hallsSection} className="py-24 md:py-32 bg-[#1A1817] border-y border-[#D4AF37]/10">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-24">
        <div>
          <p className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]">Salonlarımız</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#F5F5F0] mt-5 gold-rule">
            Sultan <span className="font-romance text-[#E5C783]">Salonları</span>
          </h2>
        </div>
        <p className="max-w-md text-sm font-light leading-relaxed text-[#B3B0A8] md:text-right">
          Sütunsuz, kademeli ve yüksek tavanlı; her açıdan mükemmel görüş sağlayacak şekilde
          tasarlanmış dört ayrı salon. Otomatik açılır sahne, özel ışıklandırma ve kanallı klima ile.
        </p>
      </div>

      <div className="space-y-20 md:space-y-28">
        {HALLS.map((hall, idx) => (
          <article
            key={hall.id}
            data-testid={HOME.hallCard(hall.id)}
            className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
              idx % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
            }`}
          >
            <div className="lg:col-span-7 relative group">
              <div className="absolute -top-5 -left-5 w-full h-full border border-[#D4AF37]/25 hidden md:block" />
              <div
                className="relative overflow-hidden cursor-pointer"
                onClick={() => openLightbox(hall, 0)}
                data-testid={`home-hall-image-${hall.id}`}
              >
                <img
                  src={hall.image}
                  alt={`${hall.name} — Neco Plaza`}
                  loading="lazy"
                  className="w-full h-[340px] md:h-[480px] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <span className="absolute top-5 left-5 bg-[#121110]/85 backdrop-blur-sm text-[#D4AF37] text-[10px] uppercase tracking-[0.25em] px-4 py-2 border border-[#D4AF37]/40">
                  {hall.area}
                </span>
                <span className="absolute bottom-5 right-5 bg-[#121110]/85 backdrop-blur-sm text-[#F5F5F0] p-2.5 border border-[#D4AF37]/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <Expand size={16} strokeWidth={1.5} />
                </span>
              </div>
              {hall.gallery && (
                <div className={`relative grid gap-4 mt-4 ${hall.gallery.length >= 3 ? "grid-cols-3" : hall.gallery.length === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
                  {hall.gallery.map((g, i) => (
                    <div
                      key={g}
                      className="overflow-hidden cursor-pointer relative group/thumb"
                      onClick={() => openLightbox(hall, i + 1)}
                      data-testid={`home-hall-thumb-${hall.id}-${i}`}
                    >
                      <img
                        src={g}
                        alt={`${hall.name} görsel ${i + 2} — Neco Plaza`}
                        loading="lazy"
                        className="w-full h-36 md:h-48 object-cover hover:scale-105 transition-transform duration-700 ease-out"
                      />
                      <span className="absolute inset-0 bg-[#121110]/0 group-hover/thumb:bg-[#121110]/30 transition-colors duration-300 flex items-center justify-center">
                        <Expand size={18} strokeWidth={1.5} className="text-[#F5F5F0] opacity-0 group-hover/thumb:opacity-100 transition-opacity duration-300" />
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-5">
              <p className="font-romance text-xl text-[#E5C783]">{hall.tagline}</p>
              <h3 className="font-display text-3xl sm:text-4xl text-[#F5F5F0] mt-3">{hall.name}</h3>
              <p className="text-sm font-light leading-relaxed text-[#B3B0A8] mt-5">{hall.description}</p>

              <div className="grid grid-cols-3 gap-px bg-[#D4AF37]/15 border border-[#D4AF37]/15 mt-8">
                {hall.stats.map((s) => (
                  <div key={s.label} className="bg-[#121110] px-4 py-5 text-center">
                    <p className="font-display text-2xl md:text-3xl text-[#D4AF37]">{s.value}</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#B3B0A8] mt-1">{s.label}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-6">
                {hall.features.map((f) => (
                  <span key={f} className="flex items-center gap-2 text-xs text-[#B3B0A8]">
                    <Check size={12} className="text-[#D4AF37] shrink-0" />
                    {f}
                  </span>
                ))}
              </div>

              <a
                href="#rezervasyon"
                className="inline-block mt-8 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#121110] px-8 py-3 text-[11px] uppercase tracking-[0.25em] transition-colors duration-300"
              >
                Bu Salon İçin Rezervasyon
              </a>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>

  {lightbox && (
    <div
      data-testid="home-gallery-lightbox"
      className="fixed inset-0 z-[100] bg-[#0E0D0C]/95 backdrop-blur-md flex flex-col items-center justify-center p-4 md:p-10 fade-up"
      onClick={close}
    >
      <button
        onClick={close}
        data-testid="home-gallery-close-button"
        aria-label="Kapat"
        className="absolute top-5 right-5 text-[#B3B0A8] hover:text-[#D4AF37] border border-[#D4AF37]/30 hover:border-[#D4AF37] p-3 transition-colors duration-300 z-10"
      >
        <X size={20} strokeWidth={1.5} />
      </button>

      <p className="font-romance text-xl text-[#E5C783] mb-1">{lightbox.hallName}</p>
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#B3B0A8] mb-5" data-testid="home-gallery-counter">
        {lightbox.index + 1} / {lightbox.images.length}
      </p>

      <div className="relative flex items-center justify-center w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
        {lightbox.images.length > 1 && (
          <button
            onClick={() => step(-1)}
            data-testid="home-gallery-prev-button"
            aria-label="Önceki"
            className="absolute -left-2 md:-left-16 text-[#B3B0A8] hover:text-[#D4AF37] border border-[#D4AF37]/30 hover:border-[#D4AF37] p-3 transition-colors duration-300 z-10"
          >
            <ChevronLeft size={20} strokeWidth={1.5} />
          </button>
        )}

        <img
          key={lightbox.images[lightbox.index]}
          src={lightbox.images[lightbox.index]}
          alt={`${lightbox.hallName} — görsel ${lightbox.index + 1}`}
          data-testid="home-gallery-image"
          className="max-h-[72vh] w-auto max-w-full object-contain border border-[#D4AF37]/25 fade-up"
        />

        {lightbox.images.length > 1 && (
          <button
            onClick={() => step(1)}
            data-testid="home-gallery-next-button"
            aria-label="Sonraki"
            className="absolute -right-2 md:-right-16 text-[#B3B0A8] hover:text-[#D4AF37] border border-[#D4AF37]/30 hover:border-[#D4AF37] p-3 transition-colors duration-300 z-10"
          >
            <ChevronRight size={20} strokeWidth={1.5} />
          </button>
        )}
      </div>

      {lightbox.images.length > 1 && (
        <div className="flex gap-2 mt-6" onClick={(e) => e.stopPropagation()}>
          {lightbox.images.map((img, i) => (
            <button
              key={img}
              onClick={() => setLightbox({ ...lightbox, index: i })}
              data-testid={`home-gallery-dot-${i}`}
              aria-label={`Görsel ${i + 1}`}
              className={`h-1 transition-all duration-300 ${
                i === lightbox.index ? "w-10 bg-[#D4AF37]" : "w-5 bg-[#B3B0A8]/40 hover:bg-[#B3B0A8]"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  )}
  </>
  );
};
